<template>
  <Box>
    <BoxHead
      icon="fas fa-book"
      :link="link"
    >
      Dossier
    </BoxHead>

    <LoaderWithText
      v-if="isFetching"
      size="3_5"
    />

    <Stack
      v-else
      class="dossier-body"
      gap="2"
    >
      <p class="completion-rate">
        <span class="completion-label">Overall completion rate</span>
        <span>
          <span
            class="completion-number"
            :style="{ color: summary.overallRateColor }"
          >{{ summary.overallRate }}</span>%</span>
      </p>

      <Stack
        v-if="summary.sections?.length"
        gap="1"
      >
        <div
          v-for="section in summary.sections"
          :key="section.name"
          class="section-item"
        >
          <p class="section-name">
            {{ section.name }}
          </p>
          <p
            class="section-count"
          >
            <span
              class="section-filled"
              :style="{ color: section.color }"
            >
              {{ section.filledFields }}</span>
            <span class="section-slash">/</span>
            <span class="section-total">{{ section.totalFields }}</span>
          </p>
        </div>
      </Stack>

      <EmptyBox
        v-else
        size="3_5"
        class="empty-dossier"
      >
        <Stack gap="2">
          <p>No Dossier sections found</p>
          <Button
            :to="toPath"
            icon-before="fas fa-book"
            spacing="tight"
          >
            Complete Dossier
          </Button>
        </Stack>
      </EmptyBox>
    </Stack>
  </Box>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { Box, BoxHead, Button, EmptyBox, LoaderWithText, Stack } from '@stakeui';
import { completionColor, prettifyMixedCase } from '@frontend/shared/Util.js';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore.js';

const advisorStore = useAdvisorStore();

const props = defineProps({
  entityExternalId: {
    type: String,
    required: true,
  },
});

const isFetching = ref(false);
const hasFetched = ref(false);
const dossier = ref({});

const isFilled = value => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string' && value.trim() === '') return false;
  return true;
};

const countFields = obj => {
  let total = 0;
  let filled = 0;

  const traverse = value => {
    if (Array.isArray(value)) {
      for (const v of value) traverse(v);
    } else if (value && typeof value === 'object') {
      for (const v of Object.values(value)) traverse(v);
    } else {
      total++;
      if (isFilled(value)) filled++;
    }
  };

  traverse(obj);
  return { total, filled };
};

const aggregateBySection = dossier => {
  const result = {
    overallRate: 0,
    overallRateColor: '#666',
    sections: [],
  };

  if (!dossier) {
    return result;
  }

  let grandTotal = 0;
  let grandFilled = 0;

  for (const [section, data] of Object.entries(dossier)) {
    if (section === 'metadata') continue;
    const { total, filled } = countFields(data);
    const completion = filled / total * 100;
    const color = completionColor(completion);

    result.sections.push({
      name: prettifyMixedCase(section),
      totalFields: total,
      filledFields: filled,
      color,
    });

    grandTotal += total;
    grandFilled += filled;
  }

  const overallRate = grandTotal ? (grandFilled / grandTotal) * 100 : 0;
  result.overallRate = Math.round(overallRate);
  result.overallRateColor = completionColor(overallRate);

  return result;
};

const fetchData = async () => {
  isFetching.value = true;

  try {
    const response = await fetch(`/api/v1/dossier/entity/${props.entityExternalId}/`, {
      method: 'GET',
      headers: advisorStore.headers,
    });


    if (!response.ok) {
      throw new Error(`Failed to fetch: ${response.status}`);
    }

    const data = await response.json();
    dossier.value = data.dossier || {};
  } catch (error) {
    console.error(`Error fetching`, error);
  } finally {
    isFetching.value = false;
    hasFetched.value = true;
  }
};

const summary = computed(() => aggregateBySection(dossier.value.dossier_data));

const toPath = { name: 'entity-dossier' };
const link = computed(() => {
  return {
    to: toPath,
    label: 'View all',
  };
});

onMounted(() => {
  fetchData();
});
</script>

<style scoped>
.dossier-body {
  flex-grow: 1;
}

.empty-dossier {
  min-height: 0;
}

.completion-rate {
  color: var(--stk-text-weak);
  display: flex;
  justify-content: space-between;
}

.completion-label {
  font-size: 0.875em;
}

.completion-number {
  font-weight: 500;
}

.section-item {
  font-size: 0.875em;
  display: flex;
  align-items: center;
  gap: 1em;
  justify-content: space-between;
}

.section-count {
  color: var(--stk-text-weak);
  display: flex;
  align-items: end;
  gap: 0.25em;
}

.section-filled {
  font-size: 500;
}

.section-slash {
  opacity: 0.5;
  font-size: 0.75em;
}

.section-total {
  color: var(--stk-text-weak);
  font-size: 0.875em;
}
</style>
